import { useMemo, useState } from 'react';
import { CircleCheck, FileWarning, Clock, ShieldX } from 'lucide-react';
import { Chip } from '@/ui/primitives';
import { useDocumentos } from '../api';

const TIPO_LABEL: Record<string, string> = {
  CCIR: 'CCIR',
  ccir: 'CCIR',
  ITR: 'ITR',
  itr: 'ITR',
  ITR_declaracao: 'ITR — Declaração',
  ITR_recibo: 'ITR — Recibo',
  irpf: 'Imposto de Renda',
  certidao_inteiro_teor: 'Certidão de Inteiro Teor',
  CAR: 'CAR',
  car: 'CAR',
  dap_caf: 'DAP/CAF',
  memorial_descritivo: 'Memorial descritivo',
  arrendamento: 'Contrato de Arrendamento',
  licenca_ambiental: 'Licença ambiental',
  proposta_credito: 'Proposta de crédito',
  rg: 'RG',
  cpf_doc: 'CPF',
  certidao_casamento: 'Certidão de Casamento',
  contrato: 'Contrato',
  cnd_nirf: 'CND NIRF',
  outros: 'Outros',
};

function tipoLabel(t: string | null | undefined): string {
  if (!t) return 'documento';
  return TIPO_LABEL[t] ?? t.replace(/_/g, ' ');
}

function diasAte(d: string): number {
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const alvo = new Date(d.length >= 10 ? d.slice(0, 10) + 'T00:00:00' : d);
  return Math.floor((alvo.getTime() - hoje.getTime()) / 86400000);
}

interface Item {
  tipo: string;
  diasRestantes: number;
}

export function DocumentosStatusIndicator({ cpf }: { cpf: string }) {
  const { data: docs = [], isLoading } = useDocumentos(cpf);
  const [hover, setHover] = useState(false);

  const { vencidos, vencendoSoon, semValidade } = useMemo(() => {
    const v: Item[] = [];
    const s: Item[] = [];
    for (const d of docs) {
      if (!d.validade) continue;
      const dias = diasAte(d.validade);
      if (dias < 0) v.push({ tipo: d.tipo ?? '', diasRestantes: dias });
      else if (dias <= 30) s.push({ tipo: d.tipo ?? '', diasRestantes: dias });
    }
    v.sort((a, b) => a.diasRestantes - b.diasRestantes);
    s.sort((a, b) => a.diasRestantes - b.diasRestantes);
    return { vencidos: v, vencendoSoon: s, semValidade: docs.length === 0 };
  }, [docs]);

  if (isLoading) return null;
  if (semValidade) return null;

  const tone =
    vencidos.length > 0 ? 'danger' : vencendoSoon.length > 0 ? 'warn' : 'ok';
  const Icon =
    vencidos.length > 0 ? ShieldX : vencendoSoon.length > 0 ? Clock : CircleCheck;
  const label =
    vencidos.length > 0
      ? `${vencidos.length} documento${vencidos.length === 1 ? '' : 's'} vencido${vencidos.length === 1 ? '' : 's'}`
      : vencendoSoon.length > 0
        ? `${vencendoSoon.length} vencendo em ≤30d`
        : 'Documentos em dia';

  const showTooltip = hover && (vencidos.length > 0 || vencendoSoon.length > 0);

  return (
    <div
      className="relative inline-flex items-center"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <Chip tone={tone} size="sm">
        <span className="inline-flex items-center gap-1">
          <Icon size={11} strokeWidth={1.75} />
          {label}
        </span>
      </Chip>
      {showTooltip && (
        <div
          role="tooltip"
          className="absolute left-0 top-full mt-1.5 z-50 min-w-[240px] rounded-md border border-border bg-elevated px-3 py-2 shadow-[var(--shadow-over)]"
        >
          {vencidos.length > 0 && (
            <>
              <div className="text-[0.7rem] font-semibold uppercase tracking-wide text-[var(--danger)] mb-1 flex items-center gap-1">
                <FileWarning size={11} strokeWidth={1.75} />
                Vencidos
              </div>
              <ul className="flex flex-col gap-0.5 text-[0.78rem] text-text mb-2">
                {vencidos.map((it, i) => (
                  <li key={`v-${i}`} className="flex items-center justify-between gap-2">
                    <span className="truncate">{tipoLabel(it.tipo)}</span>
                    <span className="text-[var(--danger)] text-[0.72rem] shrink-0">
                      {Math.abs(it.diasRestantes)}d atrás
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
          {vencendoSoon.length > 0 && (
            <>
              <div className="text-[0.7rem] font-semibold uppercase tracking-wide text-[var(--warning)] mb-1 flex items-center gap-1">
                <Clock size={11} strokeWidth={1.75} />
                Vencendo em ≤30 dias
              </div>
              <ul className="flex flex-col gap-0.5 text-[0.78rem] text-text">
                {vencendoSoon.map((it, i) => (
                  <li key={`s-${i}`} className="flex items-center justify-between gap-2">
                    <span className="truncate">{tipoLabel(it.tipo)}</span>
                    <span className="text-[var(--warning)] text-[0.72rem] shrink-0">
                      em {it.diasRestantes}d
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}
